/**
 * /wireline-slickline/tool-strings/:id — one tool string and its items
 * (GET / PATCH / DELETE /wireline-slickline/tool-strings/:id, PUT …/items;
 * fieldiq-api#1336, WL-1).
 *
 * Panels stack full width: details, items, then the header edit form when
 * editing. Edit and items are WL_GEAR_WRITE (admin, office_staff,
 * supervisor); delete is WL_ADMIN (admin, office_staff). The total length and
 * widest OD are the API's, shown as sent: the length in the tenant's unit
 * (`totalLength` + `lengthUnit`, api#1339), the OD in mm.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Pencil, Ruler, Trash2, Wrench } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { useAuth } from '../../contexts/AuthContext';
import { displayValue } from '../../features/fieldiq-handoff/contracts';
import { useDeleteWlToolString, useUpdateWlToolString, useWlMeta, useWlToolString } from '../../hooks/useWirelineSlickline';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { canAdminWl, canWriteWlGear, wlLineLength, wlRefusal, wlWithUnit } from '../../lib/wireline-slickline';
import { useBackToList } from '../downhole/recordPage';
import { ToolStringForm } from './ToolStringForm';
import { ToolStringItems } from './ToolStringItems';
import { isNotFound, WL_BAND_SECONDARY, WL_EYEBROW, WL_PRIMARY, WL_SECONDARY, WlDialog, WlEmpty, WlFormError, WlLoading, WlPanel, WlSpec } from './WlBits';

export default function WlToolStringDetailScreen() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const back = useBackToList('/wireline-slickline/tool-strings');
  const { user } = useAuth();
  const query = useWlToolString(id);
  const catalog = useWlMeta().data?.data ?? null;
  const canGear = canWriteWlGear(user?.role);
  const canAdmin = canAdminWl(user?.role);
  const update = useUpdateWlToolString();
  const del = useDeleteWlToolString();
  const [editing, setEditing] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  useEffect(() => {
    setEditing(false);
    setEditError(null);
    setDeleting(false);
  }, [id]);

  const ts = query.data?.data ?? null;
  const meta = query.data?.meta ?? null;
  const failure = queryLoadFailure(query);
  const notFound = isNotFound(queryFailureError(query));

  return (
    <div data-testid="wl-tool-string-detail-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={WL_EYEBROW}
        title={ts ? ts.name || '—' : query.isLoading ? 'Tool string' : '—'}
        onBack={back}
        backLabel="Back"
        actions={
          ts && (canGear || canAdmin) ? (
            <div className="flex flex-wrap gap-2">
              {canGear && (
                <button
                  type="button"
                  className="fi-action fi-action--standard fi-action--primary"
                  onClick={() => {
                    setEditError(null);
                    setEditing(true);
                  }}
                  disabled={editing}
                >
                  <Pencil className="size-4" aria-hidden="true" />
                  Edit details
                </button>
              )}
              {canAdmin && (
                <button type="button" className={WL_BAND_SECONDARY} onClick={() => { setDeleteError(null); setDeleting(true); }}>
                  <Trash2 className="size-4" aria-hidden="true" />
                  Delete
                </button>
              )}
            </div>
          ) : undefined
        }
        stats={[
          { icon: <Wrench className="size-4" aria-hidden="true" />, label: 'Items', tone: 'neutral', loading: query.isLoading, value: displayValue(ts?.itemCount) },
          { icon: <Ruler className="size-4" aria-hidden="true" />, label: 'Length', tone: 'neutral', loading: query.isLoading, value: ts ? wlLineLength(ts.totalLength, ts.lengthUnit) : '—' },
        ]}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {query.isLoading ? (
            <WlLoading label="Loading tool string" />
          ) : notFound || (!failure && !ts) ? (
            <WlPanel eyebrow="Tool string" title="Tool string">
              <WlEmpty>This tool string was not found. It may have been deleted.</WlEmpty>
            </WlPanel>
          ) : failure || !ts ? (
            <WlPanel eyebrow="Tool string" title="Tool string">
              <LoadFailureNotice error={queryFailureError(query)} kind={failure ?? 'unreachable'} subject="this tool string" />
            </WlPanel>
          ) : (
            <>
              <WlPanel eyebrow="Tool string" title="Details" testId="wl-tool-string-details">
                <dl className="grid gap-4 px-4 py-4 sm:grid-cols-2 lg:grid-cols-4">
                  <WlSpec label="Name" value={ts.name || '—'} />
                  <WlSpec label="Line type" value={ts.lineType ? ts.lineTypeLabel ?? ts.lineType : 'Any line'} />
                  <WlSpec label="Total length" value={wlLineLength(ts.totalLength, ts.lengthUnit)} mono />
                  <WlSpec label="Widest OD" value={wlWithUnit(ts.maxOdMm, 'mm')} mono />
                </dl>
                {ts.notes && <p className="border-t border-fieldiq-border px-4 py-3 text-sm text-fieldiq-text">{ts.notes}</p>}
              </WlPanel>

              <ToolStringItems key={ts.updatedAt ?? ts.id} toolString={ts} catalog={catalog} meta={meta} canWrite={canGear} />

              {editing && canGear && (
                <WlPanel eyebrow="Edit" title="Edit tool string" testId="wl-tool-string-edit">
                  <ToolStringForm
                    key={ts.id}
                    toolString={ts}
                    catalog={catalog}
                    pending={update.isPending}
                    error={editError}
                    submitLabel="Save tool string"
                    onCancel={() => setEditing(false)}
                    onSubmit={(body) => {
                      setEditError(null);
                      update.mutate(
                        { id: ts.id, body },
                        {
                          onSuccess: () => {
                            toast.success('Tool string saved');
                            setEditing(false);
                          },
                          onError: (err) => setEditError(wlRefusal(err, 'The tool string could not be saved.')),
                        },
                      );
                    }}
                  />
                </WlPanel>
              )}
            </>
          )}
        </div>
      </section>

      {ts && canAdmin && (
        <WlDialog
          open={deleting}
          title="Delete tool string"
          onClose={() => setDeleting(false)}
          footer={
            <>
              <button type="button" className={WL_SECONDARY} onClick={() => setDeleting(false)} disabled={del.isPending}>
                Keep tool string
              </button>
              <button
                type="button"
                className={WL_PRIMARY}
                disabled={del.isPending}
                onClick={() =>
                  del.mutate(ts.id, {
                    onSuccess: () => {
                      toast.success('Tool string deleted');
                      navigate('/wireline-slickline/tool-strings', { replace: true });
                    },
                    onError: (err) => setDeleteError(wlRefusal(err, 'The tool string could not be deleted.')),
                  })
                }
              >
                {del.isPending ? 'Deleting…' : 'Delete tool string'}
              </button>
            </>
          }
        >
          <WlFormError message={deleteError} />
          <p className="text-sm text-fieldiq-text">Removes {ts.name} and its items. Equipment linked to an item stays in Equipment.</p>
        </WlDialog>
      )}
    </div>
  );
}
